/**
 * CordovaIonicSdkPage — dash surface for the Cordova / Ionic SDK.
 * Lists installs (GET /), registers new installs (POST /register), and
 * ingests one-off events (POST /event). No delete endpoint exists.
 */
import { useState, type FormEvent } from "react";
import {
	useCordovaIonicSdkEvent,
	useCordovaIonicSdkList,
	useCordovaIonicSdkRegister,
} from "./hooks";
import type { RegisterInstallBody } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const SELECT = INPUT;
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const PILL =
	"inline-flex items-center rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-green-700";

function formatTimestamp(ts: number): string {
	if (!ts) return "—";
	return new Date(ts * 1000).toISOString().replace("T","").slice(0, 19);
}

type RegisterForm = Pick<
	RegisterInstallBody,
	"install_id" | "app_id" | "sdk_version" | "platform" | "plugin_version"
>;

const EMPTY_REGISTER: RegisterForm = {
	install_id: "",
	app_id: "",
	sdk_version: "",
	platform: "ios",
	plugin_version: "",
};

interface EventForm {
	install_id: string;
	event_id: string;
	name: string;
	payload: string;
}

const EMPTY_EVENT: EventForm = {
	install_id: "",
	event_id: "",
	name: "",
	payload: "",
};

export function CordovaIonicSdkPage() {
	const listQuery = useCordovaIonicSdkList();
	const registerMut = useCordovaIonicSdkRegister();
	const eventMut = useCordovaIonicSdkEvent();

	const [registerForm, setRegisterForm] = useState<RegisterForm>(EMPTY_REGISTER);
	const [eventForm, setEventForm] = useState<EventForm>(EMPTY_EVENT);

	const canRegister =
 registerForm.install_id.trim() &&
 registerForm.app_id.trim() &&
 registerForm.sdk_version.trim() &&
 registerForm.plugin_version.trim() &&
 !registerMut.isPending;

	const canEmitEvent =
 eventForm.install_id.trim() &&
 eventForm.event_id.trim() &&
 eventForm.name.trim() &&
 !eventMut.isPending;

	const handleRegister = (e: FormEvent) => {
 e.preventDefault();
 if (!canRegister) return;
 registerMut.mutate(
 {
 install_id: registerForm.install_id.trim(),
 app_id: registerForm.app_id.trim(),
 sdk_version: registerForm.sdk_version.trim(),
 platform: registerForm.platform,
 plugin_version: registerForm.plugin_version.trim(),
 },
 { onSuccess: () => setRegisterForm(EMPTY_REGISTER) },
 );
	};

	const handleEvent = (e: FormEvent) => {
 e.preventDefault();
 if (!canEmitEvent) return;
 let payload: unknown = {};
 if (eventForm.payload.trim()) {
 try {
 payload = JSON.parse(eventForm.payload);
 } catch {
 payload = eventForm.payload;
 }
 }
 eventMut.mutate(
 {
 install_id: eventForm.install_id.trim(),
 event_id: eventForm.event_id.trim(),
 name: eventForm.name.trim(),
 payload,
 },
 {
 onSuccess: () =>
 setEventForm({ ...EMPTY_EVENT, install_id: eventForm.install_id.trim() }),
 },
 );
	};

	const installs = listQuery.data?.items ?? [];

	return (
 <main
 data-testid="cordova-ionic-sdk-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 cordova-ionic-sdk
 </p>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 Cordova / Ionic SDK
 </h1>
 <p className="text-sm text-zinc-600">
 Register hybrid-app installs and stream one-off events into the SDK pipeline.
 </p>
 </header>

 <section className={CARD} aria-label="Register install">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Register install</h2>
 <p className="mt-1 text-xs text-zinc-500">
 Idempotent per (tenant, install_id). Re-sending the same install_id is safe.
 </p>
 <form
 onSubmit={handleRegister}
 className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
 data-testid="cordova-ionic-sdk-register-form"
 >
 <input
 type="text"
 className={INPUT}
 placeholder="install_id"
 value={registerForm.install_id}
 onChange={(e) =>
 setRegisterForm((s) => ({ ...s, install_id: e.currentTarget.value }))
 }
 disabled={registerMut.isPending}
 data-testid="cordova-ionic-sdk-install-id"
 />
 <input
 type="text"
 className={INPUT}
 placeholder="app_id"
 value={registerForm.app_id}
 onChange={(e) =>
 setRegisterForm((s) => ({ ...s, app_id: e.currentTarget.value }))
 }
 disabled={registerMut.isPending}
 data-testid="cordova-ionic-sdk-app-id"
 />
 <input
 type="text"
 className={INPUT}
 placeholder="sdk_version"
 value={registerForm.sdk_version}
 onChange={(e) =>
 setRegisterForm((s) => ({ ...s, sdk_version: e.currentTarget.value }))
 }
 disabled={registerMut.isPending}
 data-testid="cordova-ionic-sdk-sdk-version"
 />
 <input
 type="text"
 className={INPUT}
 placeholder="plugin_version"
 value={registerForm.plugin_version}
 onChange={(e) =>
 setRegisterForm((s) => ({
 ...s,
 plugin_version: e.currentTarget.value,
 }))
 }
 disabled={registerMut.isPending}
 data-testid="cordova-ionic-sdk-plugin-version"
 />
 <select
 className={SELECT}
 value={registerForm.platform}
 onChange={(e) =>
 setRegisterForm((s) => ({
 ...s,
 platform: e.currentTarget.value as RegisterForm["platform"],
 }))
 }
 disabled={registerMut.isPending}
 data-testid="cordova-ionic-sdk-platform"
 >
 <option value="ios">ios</option>
 <option value="android">android</option>
 <option value="web">web</option>
 </select>
 <button
 type="submit"
 className={BTN}
 disabled={!canRegister}
 data-testid="cordova-ionic-sdk-register-submit"
 >
 {registerMut.isPending ? "Registering." : "Register"}
 </button>
 </form>
 {registerMut.error ? (
 <p
 className="mt-2 text-xs text-red-600"
 role="alert"
 data-testid="cordova-ionic-sdk-register-error"
 >
 {registerMut.error.message}
 </p>
 ) : null}
 </section>

 <section className={CARD} aria-label="Send event">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Send event</h2>
 <p className="mt-1 text-xs text-zinc-500">
 Idempotent per (tenant, event_id). Payload accepts JSON or a raw string.
 </p>
 <form
 onSubmit={handleEvent}
 className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
 data-testid="cordova-ionic-sdk-event-form"
 >
 <input
 type="text"
 className={INPUT}
 placeholder="install_id"
 value={eventForm.install_id}
 onChange={(e) =>
 setEventForm((s) => ({ ...s, install_id: e.currentTarget.value }))
 }
 disabled={eventMut.isPending}
 data-testid="cordova-ionic-sdk-event-install-id"
 />
 <input
 type="text"
 className={INPUT}
 placeholder="event_id"
 value={eventForm.event_id}
 onChange={(e) =>
 setEventForm((s) => ({ ...s, event_id: e.currentTarget.value }))
 }
 disabled={eventMut.isPending}
 data-testid="cordova-ionic-sdk-event-id"
 />
 <input
 type="text"
 className={INPUT}
 placeholder="name (e.g. session_start)"
 value={eventForm.name}
 onChange={(e) =>
 setEventForm((s) => ({ ...s, name: e.currentTarget.value }))
 }
 disabled={eventMut.isPending}
 data-testid="cordova-ionic-sdk-event-name"
 />
 <input
 type="text"
 className={INPUT}
 placeholder='payload (JSON, e.g. {"foo":1})'
 value={eventForm.payload}
 onChange={(e) =>
 setEventForm((s) => ({ ...s, payload: e.currentTarget.value }))
 }
 disabled={eventMut.isPending}
 data-testid="cordova-ionic-sdk-event-payload"
 />
 <button
 type="submit"
 className={BTN}
 disabled={!canEmitEvent}
 data-testid="cordova-ionic-sdk-event-submit"
 >
 {eventMut.isPending ? "Sending." : "Send event"}
 </button>
 </form>
 {eventMut.error ? (
 <p
 className="mt-2 text-xs text-red-600"
 role="alert"
 data-testid="cordova-ionic-sdk-event-error"
 >
 {eventMut.error.message}
 </p>
 ) : null}
 {eventMut.isSuccess && !eventMut.error ? (
 <p
 className="mt-2 text-xs text-green-700"
 data-testid="cordova-ionic-sdk-event-ok"
 >
 Event accepted.
 </p>
 ) : null}
 </section>

 <section className={CARD} aria-label="Installs list">
 <div className="flex items-center justify-between">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Installs</h2>
 <span className="text-xs text-zinc-500">
 {installs.length} shown (most recent 50)
 </span>
 </div>

 {listQuery.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cordova-ionic-sdk-loading"
 >
 Loading.
 </p>
 ) : listQuery.error ? (
 <p
 className="mt-3 text-sm text-red-600"
 role="alert"
 data-testid="cordova-ionic-sdk-list-error"
 >
 {listQuery.error.message}
 </p>
 ) : installs.length === 0 ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cordova-ionic-sdk-empty"
 >
 No installs yet. Register one above to see it here.
 </p>
 ) : (
 <div className="mt-3 overflow-x-auto">
 <table
 className="w-full border-collapse text-left text-sm"
 data-testid="cordova-ionic-sdk-list"
 >
 <thead>
 <tr className="border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500">
 <th className="py-2 pr-3 font-medium">install_id</th>
 <th className="py-2 pr-3 font-medium">app_id</th>
 <th className="py-2 pr-3 font-medium">platform</th>
 <th className="py-2 pr-3 font-medium">sdk</th>
 <th className="py-2 pr-3 font-medium">plugin</th>
 <th className="py-2 font-medium">created</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-zinc-100">
 {installs.map((row) => (
 <tr
 key={row.id}
 data-testid={`cordova-ionic-sdk-row-${row.install_id}`}
 >
 <td className="py-2 pr-3 font-mono text-xs text-zinc-700">
 {row.install_id}
 </td>
 <td className="py-2 pr-3 font-mono text-xs text-zinc-700">
 {row.app_id}
 </td>
 <td className="py-2 pr-3">
 <span className={PILL}>{row.platform}</span>
 </td>
 <td className="py-2 pr-3 text-xs text-zinc-600">
 {row.sdk_version}
 </td>
 <td className="py-2 pr-3 text-xs text-zinc-600">
 {row.plugin_version}
 </td>
 <td className="py-2 text-xs text-zinc-500">
 {formatTimestamp(row.created_at)}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </section>
 </main>
	);
}

export default CordovaIonicSdkPage;
